import '@/styles/loginStyles.scss'; 
import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import CapTracLogo from '@/components/CapTracLogo';
import * as Sentry from '@sentry/react';

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { login, hasPermission, permissions, user, loading } = useAuth();
    const navigate = useNavigate();

    // Define mapping of dashboard routes to permission paths 
    const dashboardRoutes = {
        '/admin/home': '/admin/home',
        '/marketing/home': '/marketing/home',
        '/finance/dashboard': '/finance/dashboard',
        '/operations/dashboard': '/operations/dashboard',
        '/legal/home': '/legal/home',
        '/dashboards/executive-dash': '/dashboards/executive-dash',
        '/operations/centremanager/home': '/operations/centremanager/home',
        '/dashboards/leasing-dashboard': '/dashboards/leasing-dashboard'
    };

    // Helper function to get the appropriate landing page for the user
    const getLandingPage = () => {
        // First, check if user has a preferred home dashboard set
        const userHomeDashboard = user?.HomeDashboardRoute || user?.homeDashboardRoute;
        if (userHomeDashboard && hasPermission(userHomeDashboard)) {
            return userHomeDashboard;
        }
        // Fallback: Find the first dashboard route the user has permission for
        for (const [route, permissionPath] of Object.entries(dashboardRoutes)) {
            if (hasPermission(permissionPath)) {
                return route;
            }
        }
        // Default to /home if no dashboard permission is found
        return '/home';
    };

    // Redirect authenticated users to their home page
    useEffect(() => {
        if (!loading && user) {
            navigate(getLandingPage());
        }
    }, [user, loading, navigate]);

    useEffect(() => {
        const checkAndNavigate = () => {
            // Only run this if we're in a loading state
            if (isLoading && permissions.length > 0) {
                setIsLoading(false);
                navigate(getLandingPage());
            }
        };

        checkAndNavigate();
    }, [permissions, hasPermission, navigate, isLoading, user]);

    // Handle form submit for login
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username || !password) {
            toast.error('Username and Password are required');
            return;
        }

        try {
            setIsLoading(true);
            const success = await login(username, password, rememberMe);

            if (!success) {
                toast.error('Invalid Username or Password');
                setPassword('');
                setIsLoading(false);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Login error:', error);
            toast.error('An error occurred during login');
            setIsLoading(false);
        }
    }

    return (
        <div id="login-main-container">
            <div id="login-container">
                <div id="login-image">
                    <CapTracLogo scale={0.6} capColor="var(--primary-color)" hexagonColor="var(--primary-color)" />
                </div>
                <h2>Please Sign in</h2>
                <form onSubmit={handleSubmit}>
                    <div id="login-creds">
                        <div id="login-username" className="login-input-container">
                            <input type="email" placeholder=" " id="username" value={username} onChange={(e) => setUsername(e.target.value)} />
                            <label className="login-label" htmlFor="username">Username</label>
                        </div>
                        <div id="login-password" className="login-input-container">
                            <input type="password" placeholder=" " id="password" value={password} onChange={(e) => setPassword(e.target.value)} />
                            <label className="login-label" htmlFor="password">Password</label>
                        </div>
                    </div>
                    <div className="login-actions-row">
                        <div className="remember-me">
                            <input 
                                type="checkbox" 
                                id="rememberMe" 
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            <label htmlFor="rememberMe">Remember Me</label>
                        </div>
                        <button
                            id="login-button"
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Signing In...' : 'Sign In'}
                        </button>
                    </div>
                </form>
                <div >
                    <Link to="/reset-password">Forgot Password</Link>
                </div>
            </div>
</div>
    )
}

export default LoginPage;